import { useMemo } from 'react';

import { useFetchGSeedSpeciesById } from 'hooks/useFetchGSeedSpeciesById';
import { useFetchMSeedSpecies } from 'hooks/useFetchMSeedSpecies';
import { MergedCrop } from 'interfaces/IGlobalCatalogue';
import { composeSeedSpeciesList } from 'utils/catalogueMergeUtils';

interface UseResolvedCropOptionProps {
  cropId: string | null;
  mOrgId: string | null;
  loadedOption: MergedCrop | null;
  /** True until the crop catalogue's first page has loaded. */
  isCatalogueLoading: boolean;
}

/**
 * Resolves the Input's crop to a merged (G + M) crop even when it sits past
 * the loaded crop catalogue pages: fetches the G crop by id and applies the
 * org's M overrides. Returns `loadedOption` untouched when it is available.
 */
export const useResolvedCropOption = ({
  cropId,
  mOrgId,
  loadedOption,
  isCatalogueLoading,
}: UseResolvedCropOptionProps) => {
  const isUnresolved = !!cropId && !loadedOption;
  // The first page usually holds the crop, so only look it up once that loads
  const needsLookup = isUnresolved && !isCatalogueLoading;

  const { data: gCrop, isLoading: isGCropLoading } = useFetchGSeedSpeciesById(
    cropId,
    needsLookup
  );

  // Same query as the crop picker (cache shared); off unless a lookup is needed
  const {
    data: mCrops,
    isLoading: isMCropsLoading,
    hasNextPage: hasNextMCropPage,
    isFetchNextPageError: hasMCropPageError,
  } = useFetchMSeedSpecies(needsLookup ? mOrgId : null);
  // A failed later page counts as done, so Add is never stuck disabled
  const isMCropsComplete =
    !isMCropsLoading && (!hasNextMCropPage || hasMCropPageError);

  const resolvedOption = useMemo((): MergedCrop | null => {
    if (!needsLookup || !gCrop || !isMCropsComplete) return null;
    return (
      composeSeedSpeciesList([gCrop], mCrops ?? []).find(
        (c) => c.globalId === gCrop.globalId
      ) ?? null
    );
  }, [gCrop, isMCropsComplete, mCrops, needsLookup]);

  return {
    cropOption: loadedOption ?? resolvedOption,
    isResolving:
      isUnresolved &&
      (isCatalogueLoading || isGCropLoading || !isMCropsComplete),
  };
};
